/**
 * WHICH PLAN LEADS, AND WHEN A SECOND PLAN IS SHOWN — one rule for every module (owner, 20 Oct 2026:
 * a "close second" leaving $5,000 more for $771 less outlay "isn't a real choice"; revised the same
 * day: "Leaves the most" must sit on the plan that leaves the most).
 *
 *  1. THE PLAN THAT LEAVES THE MOST LEADS, and its card says so.
 *  2. ONE EXCEPTION — A NEAR-TIE WITH A STEP THAT CANNOT BE UNDONE. Where the top plan takes an
 *     irreversible step (an annuity, a gift, a funeral bond, selling or transferring the home) and a
 *     plan within the larger of $10,000 or 0.5% of its estate does not, that plan leads instead, and
 *     its card says what it is: "About the most, without …".
 *  3. A SECOND CARD ONLY FOR A REAL TRADE-OFF. Another plan is shown beside the lead only when it is
 *     close enough to weigh (its estate within the larger of $50,000 or 2% of the lead's) AND clearly
 *     better on something that is not the estate: at least 20% or $50,000 less money tied up (whichever
 *     is larger), or at least $1,000 a year more to spare, or it avoids an irreversible step the lead
 *     takes. Its line says what it gives up and what it gains.
 *  4. Every plan tested stays one click away ("All the ways tested"), so nothing is hidden.
 *
 * Money tied up is read from the client's side: money no longer within reach for anything else — a
 * refundable deposit counts in full (owner, 20 Oct 2026).
 *
 * Each module supplies the four readings below for its own rows; the figures live here only.
 */
import { riskKindsFromCandidateId, type RiskKind } from './strategyRisks.ts';
import type { EvaluatedStrategy } from './supportAtHomeRecommendedStrategies.ts';

export const PLAN_CHOICE = {
    tieMinUsd: 10_000,
    tieShare: 0.005,
    considerMinUsd: 50_000,
    considerShare: 0.02,
    outlayMinUsd: 50_000,
    outlayShare: 0.2,
    cashflowMinUsd: 1_000,
} as const;

/** Steps that cannot be undone if circumstances change. */
export const IRREVERSIBLE_KINDS: ReadonlyArray<RiskKind> = ['annuityNil', 'annuityIndexed', 'annuityReturned', 'annuityGuaranteed', 'gift', 'funeralBond', 'sellHome', 'sellAndRent', 'transferHome'];

const STEP_WORDS: Partial<Record<RiskKind, string>> = {
    annuityNil: 'locking money into an annuity',
    annuityIndexed: 'locking money into an annuity',
    annuityReturned: 'locking money into an annuity',
    annuityGuaranteed: 'locking money into an annuity',
    gift: 'giving money away',
    funeralBond: 'a funeral bond',
    sellHome: 'selling the home',
    sellAndRent: 'selling the home',
    transferHome: 'transferring the home',
};

export interface PlanReadings<S> {
    estate: (s: S) => number;
    /** Money the plan ties up: no longer within reach for anything else. */
    moved: (s: S) => number;
    cashflow: (s: S) => number;
    kinds: (s: S) => RiskKind[];
}

const irreversibleOf = <S>(s: S, r: PlanReadings<S>): RiskKind[] => r.kinds(s).filter((k) => IRREVERSIBLE_KINDS.includes(k));
const stepWords = (k: RiskKind | undefined): string => (k && STEP_WORDS[k]) || 'a step that cannot be undone';

/** The band within which two estates are level. */
export function estateTieBand(bestEstate: number): number {
    return Math.max(PLAN_CHOICE.tieMinUsd, Math.abs(bestEstate) * PLAN_CHOICE.tieShare);
}

const topOf = <S>(pool: S[], r: PlanReadings<S>): S => pool.reduce((top, s) => (r.estate(s) > r.estate(top) ? s : top), pool[0]!);

/**
 * Rules 1 and 2: the plan that leaves the most — unless it takes an irreversible step and a plan level
 * with it does not, when the best of those leads. `pool` is the field the goal ranks (already gated).
 */
export function leadWithinTie<S>(pool: S[], r: PlanReadings<S>): S | null {
    if (pool.length === 0) return null;
    const best = topOf(pool, r);
    if (irreversibleOf(best, r).length === 0) return best;
    const band = estateTieBand(r.estate(best));
    const clean = pool.filter((s) => r.estate(best) - r.estate(s) <= band && irreversibleOf(s, r).length === 0);
    return clean.length ? topOf(clean, r) : best;
}

/** When the lead is NOT the plan that leaves the most (rule 2): by how much, and the step it avoids. */
export interface LeadException { gap: number; avoids: string }

export function leadExceptionOf<S>(pool: S[], lead: S | null, r: PlanReadings<S>): LeadException | null {
    if (!lead || pool.length === 0) return null;
    const best = topOf(pool, r);
    const gap = r.estate(best) - r.estate(lead);
    if (!(gap >= 1)) return null;
    const avoided = irreversibleOf(best, r).filter((k) => !irreversibleOf(lead, r).includes(k));
    return { gap, avoids: stepWords(avoided[0]) };
}

/** The lead's chip: "Leaves the most" is said only of the plan that does. */
export const LEADS_THE_MOST_CHIP = 'Leaves the most';
export function leadChip(ex: LeadException | null | undefined): string {
    return ex ? `About the most, without ${ex.avoids}` : LEADS_THE_MOST_CHIP;
}
/** The lead's line in the exception: what it gives up, and why it leads anyway. */
export function leadLine(ex: LeadException, formatCurrency: (n: number) => string): string {
    return `Leaves ${formatCurrency(Math.round(ex.gap))} less than the plan that leaves the most, which involves ${ex.avoids}. The two are close enough to call level, so the plan without a step that cannot be undone comes first.`;
}

export type TradeOff =
    | { kind: 'moved'; saving: number }
    | { kind: 'cashflow'; gain: number }
    | { kind: 'reversible'; avoids: string };

/** Rule 3's test: what makes `other` worth a card beside `lead`, or null. */
export function tradeOffOf<S>(lead: S, other: S, r: PlanReadings<S>): TradeOff | null {
    const saving = r.moved(lead) - r.moved(other);
    if (saving >= Math.max(PLAN_CHOICE.outlayMinUsd, r.moved(lead) * PLAN_CHOICE.outlayShare)) return { kind: 'moved', saving };
    const gain = r.cashflow(other) - r.cashflow(lead);
    if (gain >= PLAN_CHOICE.cashflowMinUsd) return { kind: 'cashflow', gain };
    const avoided = irreversibleOf(lead, r).filter((k) => !irreversibleOf(other, r).includes(k));
    if (avoided.length > 0) return { kind: 'reversible', avoids: stepWords(avoided[0]) };
    return null;
}

export interface SecondPlan<S> {
    strategy: S;
    tradeOff: TradeOff;
    /** How much less it leaves to the family than the lead (negative when it leaves more). */
    estateGap: number;
    /** Its yearly cashflow and the lead's, for the "further short" / "less to spare" words. */
    cash: number;
    leadCash: number;
    /** The irreversible step it takes that the lead does not, if any. */
    takes: string | null;
}

/**
 * Rule 3: the plan, if any, that earns a card beside the lead — close enough to weigh, and a real
 * trade-off. Of several, the closest to the lead's estate; then the one tying up least.
 */
export function pickSecondPlan<S>(pool: S[], lead: S | null, r: PlanReadings<S>, sameAs: (a: S, b: S) => boolean): SecondPlan<S> | null {
    if (!lead) return null;
    const leadEstate = r.estate(lead);
    const reach = Math.max(PLAN_CHOICE.considerMinUsd, Math.abs(leadEstate) * PLAN_CHOICE.considerShare);
    const options = pool
        .filter((s) => !sameAs(s, lead) && leadEstate - r.estate(s) <= reach)
        .map((s) => ({ s, t: tradeOffOf(lead, s, r) }))
        .filter((x): x is { s: S; t: TradeOff } => x.t != null)
        .sort((a, b) => (r.estate(b.s) - r.estate(a.s)) || (r.moved(a.s) - r.moved(b.s)));
    const pick = options[0];
    if (!pick) return null;
    const takes = irreversibleOf(pick.s, r).filter((k) => !irreversibleOf(lead, r).includes(k));
    return {
        strategy: pick.s,
        tradeOff: pick.t,
        estateGap: leadEstate - r.estate(pick.s),
        cash: r.cashflow(pick.s),
        leadCash: r.cashflow(lead),
        takes: takes.length ? stepWords(takes[0]) : null,
    };
}

/**
 * The second card's one line: what it gives up, then what it gains (owner's wording, 20 Oct 2026:
 * "Leaves $11,887 less and is $2,910 a year further short, but keeps $100,000 more within reach.").
 */
export function secondPlanLine(second: SecondPlan<unknown>, formatCurrency: (n: number) => string): string {
    const f = (v: number) => formatCurrency(Math.round(Math.abs(v)));
    const costs: string[] = [];
    const gains: string[] = [];
    const t = second.tradeOff;
    const d = second.cash - second.leadCash;
    /*
     * PLAIN WORDS, AGAINST A NAMED PLAN (owner, 23 Oct 2026: "Leaves $1,288 less and is $1,631 a year further short"
     * was confusing): what it is measured against comes first, and "short" is said as money needed from savings.
     */
    if (second.estateGap >= 1) costs.push(`leaves ${f(second.estateGap)} less to the family`);
    else if (second.estateGap <= -1) gains.push(`leaves ${f(second.estateGap)} more to the family`);
    if (d <= -1) costs.push(second.cash < 0 ? `needs ${f(d)} a year more from savings` : `has ${f(d)} a year less to spare`);
    if (second.takes) costs.push(`involves ${second.takes}, which cannot be undone`);
    if (t.kind === 'moved') gains.push(`keeps ${f(t.saving)} more within reach (not tied up)`);
    if (d >= 1) gains.push(second.cash < 0 ? `needs ${f(d)} a year less from savings` : `has ${f(d)} a year more to spare`);
    if (t.kind === 'reversible') gains.push(`avoids ${t.avoids}, which cannot be undone`);
    if (!costs.length) return `Against the other plan shown, it ${gains.join(' and ')}.`;
    return `Against the other plan shown, it ${costs.join(' and ')}, but ${gains.join(' and ')}.`;
}

/** The chip on the second card. */
export const SECOND_PLAN_CHIP = 'Nearly the most, a real trade-off you should consider';
/** The second card's chip: in the exception (rule 2) the second can be the plan that leaves the most. */
export function secondPlanChip(second: SecondPlan<unknown>): string {
    return second.estateGap <= -1 ? 'Leaves the most, with a step that cannot be undone' : SECOND_PLAN_CHIP;
}

/** One line for every plans screen: the client can open every plan tested themselves. */
export function allPlansOpenLine(count: number): string {
    return `We show the plans that lead each goal, and a second only when it offers a real trade-off. All ${count.toLocaleString()} plans tested are yours to open and compare.`;
}

const usd = (v: number) => `$${v.toLocaleString('en-AU')}`;
const pctOf = (v: number) => `${+(v * 100).toFixed(1)}%`;

/**
 * The rule in words, for every Assumptions page, dialog and advice document — built from the figures
 * above so the words cannot drift from what the ranking does.
 */
export const PLAN_CHOICE_RULE_LINES: ReadonlyArray<{ label: string; value: string; text: string }> = [
    {
        label: 'A near-tie is a tie',
        value: `Within ${usd(PLAN_CHOICE.tieMinUsd)} or ${pctOf(PLAN_CHOICE.tieShare)}, whichever is larger`,
        text: `Two plans whose estates are within ${usd(PLAN_CHOICE.tieMinUsd)} or ${pctOf(PLAN_CHOICE.tieShare)} of the estate (whichever is larger) are treated as level: the projection cannot honestly tell them apart. A tie matters only where one of them takes a step that cannot be undone.`,
    },
    {
        label: 'The plan that leaves the most leads',
        value: 'Unless it takes a step that cannot be undone',
        text: 'The first card is the plan that leaves the most to the family. The one exception: where that plan takes a step that cannot be undone (an annuity, a gift, a funeral bond, selling or transferring the home) and a plan level with it does not, the plan without it comes first, and its card says so.',
    },
    {
        label: 'A second plan only for a real trade-off',
        value: `Within ${usd(PLAN_CHOICE.considerMinUsd)} or ${pctOf(PLAN_CHOICE.considerShare)}, and clearly better on something else`,
        text: `Beside the plan that leaves the most, a second is shown only when its estate is within ${usd(PLAN_CHOICE.considerMinUsd)} or ${pctOf(PLAN_CHOICE.considerShare)} (whichever is larger) and it is clearly better on something else: at least ${pctOf(PLAN_CHOICE.outlayShare)} or ${usd(PLAN_CHOICE.outlayMinUsd)} less money tied up (whichever is larger; money tied up is money no longer within reach, a refundable deposit included), at least ${usd(PLAN_CHOICE.cashflowMinUsd)} a year more to spare, or it avoids a step that cannot be undone. Its card says what it gives up and what it gains, in one line.`,
    },
    {
        label: 'Every plan is yours to open',
        value: 'All plans tested, or build your own',
        text: 'The cards are a shortlist, not the only options. Every plan tested can be opened and compared under "All the ways tested", and you can build a plan yourself with your own figures.',
    },
];

/* ─── Reading "money moved" from a candidate's inputs (village, downsizer, granny flat, cashflow) ─── */

const n = (v: unknown) => { const x = parseFloat(String(v ?? '')); return Number.isFinite(x) && x > 0 ? x : 0; };

/**
 * The amounts a candidate puts somewhere new: gifts, bonds, an annuity, a property, shares, super
 * contributions, a new home, an entry contribution or a transfer, or the money taken from accounts.
 * Read defensively — each engine names its fields its own way.
 */
export function movedFromInputs(row: unknown): number {
    const r = (row || {}) as Record<string, unknown>;
    const inputs = (r.inputs || r.config || r) as Record<string, unknown>;
    const sh = (inputs.sellHomeDetails || {}) as Record<string, unknown>;
    const dep = (inputs.deployment || {}) as Record<string, unknown>;
    const sources = Array.isArray(inputs.fundingSources) ? (inputs.fundingSources as Array<Record<string, unknown>>) : [];
    const fromSources = sources.reduce((t, s) => t + n(s.amountToTake), 0);
    const named = [
        sh.giftingAmount, sh.funeralBondPurchase, sh.purchaseAnnuity, sh.purchaseInvestmentProperty, sh.purchaseShares, sh.downsizerContribution, sh.partnerDownsizerContribution,
        inputs.giftingAmount, inputs.funeralBondPurchase, inputs.newHomePurchasePrice, inputs.downsizerContribution, inputs.partnerDownsizerContribution,
        inputs.amountPaid, inputs.propertyTransferredValue, inputs.constructionCost,
        dep.annuity, dep.shares, dep.investmentProperty, dep.superContribution, dep.partnerSuperContribution, dep.investmentBond,
    ].reduce<number>((t, v) => t + n(v), 0);
    return Math.max(fromSources, named);
}

/** The readings for the engines whose rows carry a candidate id and inputs: village, downsizer, granny flat. */
export const candidateReadings: PlanReadings<EvaluatedStrategy> = {
    estate: (s) => Number(s.snapshot?.legacyValue) || 0,
    moved: (s) => movedFromInputs(s),
    cashflow: (s) => Number(s.snapshot?.netCashflow ?? s.snapshot?.cashflow ?? s.snapshot?.cashFlow) || 0,
    kinds: (s) => riskKindsFromCandidateId(String(s.id ?? '')),
};
